import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { Button, Card } from "@chef/components";
import {
  selectFirstDeliveryTimeblockId,
  selectSignupDeliveryData,
  TimeblocksByRangeWithGeoQuery,
} from "@chef/state-management";
import { isEmptyArray } from "@chef/utils/array";
import { removeDuplicatesByKey } from "@chef/utils/removeDuplicates";

import { DatePicker, Modal } from "@chef/smart-components";

import { SelectDeliveryTime } from "./SelectDeliveryTime";

import { intl } from "./ChangeFirstDeliveryDateModal.Intl";

interface ChangeFirstDeliveryDateModalProps {
  show: boolean;
  onClose: () => void;
  onSave: ({
    firstDelivery,
    firstDeliveryTimeblockId,
  }: {
    firstDelivery: string;
    firstDeliveryTimeblockId: number;
  }) => void;
  timeblocks: TimeblocksByRangeWithGeoQuery["timeblocksByRangeWithGeo"]["weeks"][number]["timeblocks"];
}

export const ChangeFirstDeliveryDateModal = ({
  show,
  onClose,
  onSave,
  timeblocks,
}: ChangeFirstDeliveryDateModalProps) => {
  const { firstDelivery } = useSelector(selectSignupDeliveryData);
  const firstDeliveryTimeblockId = useSelector(selectFirstDeliveryTimeblockId);
  const [selectedFirstDelivery, setSelectedFirstDelivery] =
    useState(firstDelivery);

  const [
    selectedFirstDeliveryTimeblockId,
    setSelectedFirstDeliveryTimeblockId,
  ] = useState(firstDeliveryTimeblockId);

  useEffect(() => {
    setSelectedFirstDelivery(firstDelivery);
    setSelectedFirstDeliveryTimeblockId(firstDeliveryTimeblockId);
  }, [show]);

  const sortedTimeblocks = useMemo(
    () =>
      timeblocks.sort((a, b) => {
        if (a.firstDeliveryDate < b.firstDeliveryDate) {
          return -1;
        }
        if (a.firstDeliveryDate > b.firstDeliveryDate) {
          return 1;
        }
        return 0;
      }),
    [timeblocks],
  );

  const availableTimeblocks = sortedTimeblocks.filter(
    (tb) => tb.firstDeliveryDate === selectedFirstDelivery && !tb.noDelivery,
  );

  const firstDeliveryOptions = removeDuplicatesByKey(
    timeblocks,
    "firstDeliveryDate",
  ).map((timeblock) => timeblock.firstDeliveryDate);

  const handleChangeFirstDelivery = (firstDeliveryDate: string) => {
    setSelectedFirstDelivery(firstDeliveryDate);
    const selectedTimeblockId = timeblocks.find(
      (tb) => tb.firstDeliveryDate === firstDeliveryDate && !tb.noDelivery,
    )?.timeblockId;

    if (selectedTimeblockId) {
      setSelectedFirstDeliveryTimeblockId(selectedTimeblockId);
    }
  };

  const handleSubmit = () => {
    onSave({
      firstDelivery: selectedFirstDelivery || "",
      firstDeliveryTimeblockId: selectedFirstDeliveryTimeblockId,
    });
    onClose();
  };

  return (
    <Modal
      name="change-first-delivery-date-modal"
      onClose={onClose}
      open={show}
      backToTop={false}
      size="sm"
      animation="fly-up"
      gesture="pull-down-to-close"
    >
      <Card
        noShadow
        noPadding
        className="flex !rounded-lg flex-col items-center justify-center w-full bg-inherit pt-8 pb-6 px-4 md:mt-32"
        id="change-first-delivery-date-card"
      >
        <h1 className="mb-4 text-xl">
          <strong>{intl.YOUR_FIRST_DELIVERY}</strong>
        </h1>
        <DatePicker
          className="flex justify-center w-full mb-6"
          availableDays={firstDeliveryOptions}
          onChange={handleChangeFirstDelivery}
          value={selectedFirstDelivery}
        />

        <SelectDeliveryTime
          timeblocks={availableTimeblocks}
          onChange={setSelectedFirstDeliveryTimeblockId}
          selectedTimeblockId={selectedFirstDeliveryTimeblockId}
        />

        <Button
          className="w-full mb-3 lg:w-64"
          onClick={handleSubmit}
          loading={false}
          disabled={!!isEmptyArray(availableTimeblocks)}
          primary
          id="save-changes-button"
        >
          {intl.SAVE_CHNAGES_BUTTON}
        </Button>
      </Card>
    </Modal>
  );
};
